import React from 'react';
import { BookOpen, Sparkles, CheckCircle } from 'lucide-react';
import { ProgressBar } from './ProgressBar';
import { LoadingSpinner } from './LoadingSpinner';
type GenerationStage = 'analyzing' | 'generating' | 'illustrating' | 'finalizing' | 'complete';
type StorybookGenerationProgressProps = {
  stage: GenerationStage;
  currentPage: number;
  totalPages: number;
  estimatedTimeRemaining: number;
};
export function StorybookGenerationProgress({
  stage,
  currentPage,
  totalPages,
  estimatedTimeRemaining
}: StorybookGenerationProgressProps) {
  const progress = currentPage / totalPages * 100;
  const stageLabels = {
    analyzing: 'Analyzing your story...',
    generating: `Generating page ${currentPage} of ${totalPages}...`,
    illustrating: `Creating illustrations for page ${currentPage}...`,
    finalizing: 'Finalizing your storybook...',
    complete: 'Your storybook is ready!'
  };
  const formatTime = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s`;
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-2319">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 p-8" data-id="element-2320">
        <div className="flex items-center justify-center mb-6" data-id="element-2321">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2322">
            {stage === 'complete' ? <CheckCircle className="w-8 h-8 text-green-600" data-id="element-2323" /> : <BookOpen className="w-8 h-8 text-green-600" data-id="element-2324" />}
          </div>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2" data-id="element-2325">
          {stage === 'complete' ? 'Storybook Complete!' : 'Creating Your Storybook'}
        </h2>

        <p className="text-center text-gray-600 mb-6" data-id="element-2326">{stageLabels[stage]}</p>

        {stage !== 'complete' && <>
            <ProgressBar progress={progress} showPercentage={true} data-id="element-2327" />

            <div className="mt-6 flex items-center justify-between text-sm" data-id="element-2328">
              <div className="flex items-center gap-2 text-gray-600" data-id="element-2329">
                <Sparkles className="w-4 h-4" data-id="element-2330" />
                <span data-id="element-2331">Using AI to craft your story</span>
              </div>
              <span className="text-gray-500" data-id="element-2332">
                ~{formatTime(estimatedTimeRemaining)} remaining
              </span>
            </div>

            <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200" data-id="element-2333">
              <p className="text-sm text-blue-900" data-id="element-2334">
                <strong data-id="element-2335">Tip:</strong> You can close this window and we'll notify
                you when your storybook is ready!
              </p>
            </div>
          </>}

        {stage === 'complete' && <button className="w-full mt-6 py-3 bg-[#4ade80] text-white font-semibold rounded-xl hover:bg-[#22c55e] transition-colors" data-id="element-2336">
            View Your Storybook
          </button>}
      </div>
    </div>;
}